import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import { WorkReport, WorkReportAttachment, WorkReportPayload } from '../types/workReport';
import { DEFAULT_PAGE_SIZE, PageResponse } from '../types/page';

export async function fetchWorkReports(params?: {
  page?: number;
  size?: number;
}): Promise<PageResponse<WorkReport>> {
  const { data } = await apiClient.get<PageResponse<WorkReport>>('/work-reports', {
    params: {
      page: params?.page ?? 0,
      size: params?.size ?? DEFAULT_PAGE_SIZE,
    },
  });
  return data;
}

export async function fetchWorkReport(workReportId: number): Promise<WorkReport> {
  const { data } = await apiClient.get<WorkReport>(`/work-reports/${workReportId}`);
  return data;
}

export async function createWorkReport(payload: WorkReportPayload): Promise<WorkReport> {
  const { data } = await apiClient.post<WorkReport>('/work-reports', payload);
  return data;
}

export async function updateWorkReport(
  workReportId: number,
  payload: WorkReportPayload
): Promise<WorkReport> {
  const { data } = await apiClient.patch<WorkReport>(`/work-reports/${workReportId}`, payload);
  return data;
}

export async function deleteWorkReport(workReportId: number): Promise<void> {
  await apiClient.delete(`/work-reports/${workReportId}`);
}

export async function approveWorkReport(workReportId: number): Promise<WorkReport> {
  const { data } = await apiClient.post<WorkReport>(`/work-reports/${workReportId}/approve`);
  return data;
}

export async function revokeWorkReport(workReportId: number): Promise<WorkReport> {
  const { data } = await apiClient.post<WorkReport>(`/work-reports/${workReportId}/revoke`);
  return data;
}

export async function uploadWorkReportAttachments(
  workReportId: number,
  files: File[]
): Promise<WorkReportAttachment[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const { data } = await apiClient.post<WorkReportAttachment[]>(
    `/work-reports/${workReportId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteWorkReportAttachment(
  workReportId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/work-reports/${workReportId}/attachments/${attachmentId}`);
}

export async function downloadWorkReportAttachment(
  workReportId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/work-reports/${workReportId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadWorkReportPdf(
  workReportId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/work-reports/${workReportId}/pdf`,
    fileName || `업무보고서_${workReportId}.pdf`
  );
}
